import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useFizbo } from '@/hooks/useFizbo';
import { portfolios } from '@/mocks/portfolioData';

interface MatchResult {
  id: string;
  type: 'FİZBO' | 'Portföy';
  title: string;
  location: string;
  price: number;
  matchScore: number;
  highlights: string[];
  propertyType: string;
  transactionType: string;
  roomCount?: string;
}

const odaSayilari = ['Tümü', 'Stüdyo', '1+0', '1+1', '2+0', '2+1', '3+0', '3+1', '4+0', '4+1', '5+1', '6+1', '7+1', '8+1', '9+1'];

const samsunDistricts = [
  'Tümü',
  'Alaçam',
  'Asarcık',
  'Atakum',
  'Ayvacık',
  'Bafra',
  'Canik',
  'Çarşamba',
  'Havza',
  'İlkadım',
  'Kavak',
  'Ladik',
  'Ondokuzmayıs',
  'Salıpazarı',
  'Tekkeköy',
  'Terme',
  'Vezirköprü',
  'Yakakent',
];

export default function TalepBulPage() {
  const { isDark } = useTheme();
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<MatchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Filters
  const [filterDistrict, setFilterDistrict] = useState('Tümü');
  const [filterType, setFilterType] = useState('Tümü');
  const [filterRoom, setFilterRoom] = useState('Tümü');
  const [filterTransaction, setFilterTransaction] = useState('Tümü');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { listings } = useFizbo();

  const handleSearch = () => {
    setIsSearching(true);
    setHasSearched(true);

    setTimeout(() => {
      const searchTerms = query.toLowerCase().trim();

      let fizboMatches = listings
        .filter((l) => {
          const matchesText = !searchTerms ||
            l.ownerName.toLowerCase().includes(searchTerms) ||
            l.location.toLowerCase().includes(searchTerms) ||
            l.propertyType.toLowerCase().includes(searchTerms) ||
            l.listingNo.toLowerCase().includes(searchTerms) ||
            (l.roomCount && l.roomCount.toLowerCase().includes(searchTerms));

          const matchesDistrict = filterDistrict === 'Tümü' || l.location.includes(filterDistrict);
          const matchesType = filterType === 'Tümü' || l.propertyType === filterType;
          const matchesRoom = filterRoom === 'Tümü' || l.roomCount === filterRoom;
          const matchesTransaction = filterTransaction === 'Tümü' || l.transactionType === filterTransaction;
          const matchesMinPrice = !minPrice || (l.price && l.price >= Number(minPrice));
          const matchesMaxPrice = !maxPrice || (l.price && l.price <= Number(maxPrice));

          return matchesText && matchesDistrict && matchesType && matchesRoom && matchesTransaction && matchesMinPrice && matchesMaxPrice;
        })
        .map((item, idx) => ({
          id: `fzb-${item.id}`,
          type: 'FİZBO' as const,
          title: `${item.ownerName} - ${item.propertyType}${item.roomCount ? ` (${item.roomCount})` : ''}`,
          location: item.location,
          price: item.price,
          matchScore: 85 + idx * 3,
          highlights: ['Lokasyon', 'Fiyat Araligi', 'Mulk Turu'],
          propertyType: item.propertyType,
          transactionType: item.transactionType,
          roomCount: item.roomCount,
        }));

      let portfolioMatches = portfolios
        .filter((p) => {
          const matchesText = !searchTerms ||
            p.title.toLowerCase().includes(searchTerms) ||
            p.location.toLowerCase().includes(searchTerms) ||
            p.propertyType.toLowerCase().includes(searchTerms);

          const matchesDistrict = filterDistrict === 'Tümü' || p.location.includes(filterDistrict);
          const matchesType = filterType === 'Tümü' || p.propertyType === filterType;
          const matchesTransaction = filterTransaction === 'Tümü' || p.transactionType === filterTransaction;
          const matchesMinPrice = !minPrice || p.price >= Number(minPrice);
          const matchesMaxPrice = !maxPrice || p.price <= Number(maxPrice);

          return matchesText && matchesDistrict && matchesType && matchesTransaction && matchesMinPrice && matchesMaxPrice;
        })
        .map((item, idx) => ({
          id: `prt-${item.id}`,
          type: 'Portföy' as const,
          title: item.title,
          location: item.location,
          price: item.price,
          matchScore: 78 + idx * 4,
          highlights: ['Lokasyon', 'Mulk Turu'],
          propertyType: item.propertyType,
          transactionType: item.transactionType,
        }));

      setResults([...fizboMatches, ...portfolioMatches]);
      setIsSearching(false);
    }, 800);
  };

  const clearFilters = () => {
    setFilterDistrict('Tümü');
    setFilterType('Tümü');
    setFilterRoom('Tümü');
    setFilterTransaction('Tümü');
    setMinPrice('');
    setMaxPrice('');
    setQuery('');
  };

  const exampleQueries = [
    "Tekkeköy'de 5 milyona dükkan",
    "Atakum'da 3+1 kiralık daire",
    "İlkadım'da villa satılık",
    "Bafra'da 1 milyon altı arsa",
  ];

  const inputClass = `w-full px-3 py-2 rounded-lg text-sm border outline-none transition-colors ${
    isDark
      ? 'bg-dark-card border-dark-border text-dark-text placeholder:text-dark-muted focus:border-primary-500'
      : 'bg-white border-light-border text-light-text placeholder:text-light-muted focus:border-primary-500'
  }`;

  const selectClass = `px-3 py-2 rounded-lg text-sm border outline-none transition-colors cursor-pointer ${
    isDark
      ? 'bg-dark-card border-dark-border text-dark-text focus:border-primary-500'
      : 'bg-white border-light-border text-light-text focus:border-primary-500'
  }`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary-500 flex items-center justify-center">
          <div className="w-7 h-7 flex items-center justify-center">
            <i className="ri-search-line text-white text-2xl" />
          </div>
        </div>
        <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          Talep Bul
        </h1>
        <p className={`text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          Dogal dil ile arayin, eslesen FIZBO ve portfoyleri aninda bulun
        </p>
      </div>

      {/* Search Box */}
      <div className="max-w-3xl mx-auto">
        <div
          className={`relative rounded-2xl border-2 transition-all ${
            isDark
              ? 'bg-dark-card border-dark-border focus-within:border-primary-500'
              : 'bg-white border-light-border focus-within:border-primary-500'
          }`}
        >
          <div className="flex items-center gap-3 p-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-search-line text-xl ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Orn: Tekkekoy'de 5 milyona dukkan..."
              className={`flex-1 bg-transparent text-base outline-none ${
                isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
              }`}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className={`w-6 h-6 flex items-center justify-center rounded-full ${
                  isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'
                }`}
              >
                <i className="ri-close-line" />
              </button>
            )}
            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="py-2.5 px-5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white font-medium text-sm rounded-xl transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              {isSearching ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line" />
                </div>
              )}
              Ara
            </button>
          </div>
        </div>

        {/* Filter Toggle + Example Queries */}
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
              showFilters
                ? 'border-primary-500 text-primary-500'
                : isDark
                ? 'border-dark-border text-dark-muted hover:border-primary-500 hover:text-primary-500'
                : 'border-light-border text-light-muted hover:border-primary-500 hover:text-primary-500'
            }`}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-filter-3-line" />
            </div>
            {showFilters ? 'Filtreleri Gizle' : 'Filtreleri Goster'}
          </button>

          {!hasSearched && (
            <div className="flex flex-wrap gap-2">
              {exampleQueries.map((q) => (
                <button
                  key={q}
                  onClick={() => { setQuery(q); }}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    isDark
                      ? 'border-dark-border text-dark-muted hover:border-primary-500 hover:text-primary-500'
                      : 'border-light-border text-light-muted hover:border-primary-500 hover:text-primary-500'
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className={`mt-3 rounded-xl border p-4 ${isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-light-border'}`}>
            <div className="flex items-center justify-between mb-3">
              <p className={`text-xs font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>Gelismis Filtreleme</p>
              <button
                onClick={clearFilters}
                className="text-xs text-primary-500 hover:text-primary-600 flex items-center gap-1"
              >
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-refresh-line" />
                </div>
                Sifirla
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Ilce</label>
                <select value={filterDistrict} onChange={(e) => setFilterDistrict(e.target.value)} className={selectClass}>
                  {samsunDistricts.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Mulk Turu</label>
                <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className={selectClass}>
                  <option value="Tümü">Tümü</option>
                  <option value="Daire">Daire</option>
                  <option value="Villa">Villa</option>
                  <option value="Dükkan">Dükkan</option>
                  <option value="Arsa">Arsa</option>
                  <option value="İşyeri">İşyeri</option>
                </select>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Oda Sayisi</label>
                <select value={filterRoom} onChange={(e) => setFilterRoom(e.target.value)} className={selectClass}>
                  {odaSayilari.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Islem Tipi</label>
                <select value={filterTransaction} onChange={(e) => setFilterTransaction(e.target.value)} className={selectClass}>
                  <option value="Tümü">Tümü</option>
                  <option value="Satılık">Satılık</option>
                  <option value="Kiralık">Kiralık</option>
                </select>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Min. Fiyat (₺)</label>
                <input
                  type="number"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  placeholder="0"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Max. Fiyat (₺)</label>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  placeholder="∞"
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {hasSearched && (
        <div className="max-w-3xl mx-auto space-y-4">
          {/* Stats */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className={`text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              {isSearching ? 'Araniyor...' : `${results.length} eslesme bulundu`}
            </p>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400`}>
                FIZBO: {results.filter((r) => r.type === 'FİZBO').length}
              </span>
              <span className={`text-xs px-2 py-1 rounded-full bg-secondary-100 text-secondary-700 dark:bg-secondary-900/30 dark:text-secondary-400`}>
                Portfoy: {results.filter((r) => r.type === 'Portföy').length}
              </span>
            </div>
          </div>

          {/* Result Cards */}
          {!isSearching && results.map((result) => (
            <div
              key={result.id}
              className={`rounded-xl p-4 md:p-5 border transition-all hover:shadow-soft ${
                isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-light-border'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                    result.type === 'FİZBO'
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'bg-secondary-100 text-secondary-700 dark:bg-secondary-900/30 dark:text-secondary-400'
                  }`}>
                    {result.type}
                  </span>
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                    {result.title}
                  </h3>
                </div>
                <div className="flex items-center gap-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    result.matchScore >= 85
                      ? 'bg-green-100 dark:bg-green-900/30'
                      : result.matchScore >= 70
                      ? 'bg-yellow-100 dark:bg-yellow-900/30'
                      : 'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    <span className={`text-sm font-bold ${
                      result.matchScore >= 85
                        ? 'text-green-700 dark:text-green-400'
                        : result.matchScore >= 70
                        ? 'text-yellow-700 dark:text-yellow-400'
                        : 'text-gray-700 dark:text-gray-400'
                    }`}>
                      %{result.matchScore}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                {result.highlights.map((h) => (
                  <span
                    key={h}
                    className={`text-xs px-2 py-1 rounded-full ${
                      isDark ? 'bg-dark-bg text-dark-muted' : 'bg-light-bg text-light-muted'
                    }`}
                  >
                    <i className="ri-check-line mr-1 text-primary-500" />
                    {h}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-4 text-xs flex-wrap">
                  <span className={isDark ? 'text-dark-muted' : 'text-light-muted'}>
                    <i className="ri-map-pin-line mr-1" />
                    {result.location}
                  </span>
                  <span className={isDark ? 'text-dark-muted' : 'text-light-muted'}>
                    <i className="ri-building-line mr-1" />
                    {result.propertyType}
                  </span>
                  {result.roomCount && (
                    <span className={isDark ? 'text-dark-muted' : 'text-light-muted'}>
                      <i className="ri-door-open-line mr-1" />
                      {result.roomCount}
                    </span>
                  )}
                  <span className={isDark ? 'text-dark-muted' : 'text-light-muted'}>
                    <i className="ri-exchange-line mr-1" />
                    {result.transactionType}
                  </span>
                </div>
                <p className={`text-sm font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                  {result.price > 0 ? `${result.price.toLocaleString('tr-TR')} ₺` : 'Fiyat Belirsiz'}
                </p>
              </div>
            </div>
          ))}

          {!isSearching && results.length === 0 && (
            <div className={`text-center py-12 rounded-xl border ${isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-light-border'}`}>
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <i className="ri-search-2-line text-xl text-gray-400" />
              </div>
              <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                Eslesme bulunamadi
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Filtreleri veya arama terimlerini degistirmeyi deneyin
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}